import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ConfirmButton from '@sayyesschool/frontend/components/confirm-button';
import EnrollmentSchedule from '@sayyesschool/frontend/components/enrollment-schedule';
import FormDialog from '@sayyesschool/frontend/components/form-dialog';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import Page from '@sayyesschool/frontend/components/page';
import { DomainLabel } from '@sayyesschool/frontend/data/common';
import { getEnrollmentHHUrl, useEnrollment } from '@sayyesschool/frontend/features/enrollments';
import { useBoolean } from '@sayyesschool/frontend/hooks/state';
import { Flex, Grid } from '@sayyesschool/frontend/ui-components';

import EnrollmentComments from '@/components/enrollments/enrollment-comments';
import EnrollmentCourses from '@/components/enrollments/enrollment-courses';
import EnrollmentDetails from '@/components/enrollments/enrollment-details';
import EnrollmentForm from '@/components/enrollments/enrollment-form';
import EnrollmentLessons from '@/components/enrollments/enrollment-lessons';
import EnrollmentMaterials from '@/components/enrollments/enrollment-materials';
import EnrollmentMeta from '@/components/enrollments/enrollment-meta';
import EnrollmentStatus from '@/components/enrollments/enrollment-status';
import EnrollmentTasks from '@/components/enrollments/enrollment-tasks';

// import EnrollmentTrialLesson from '@/components/enrollments/enrollment-trial-lesson';
import './EnrollmentPage.scss';

export default function EnrollmentPage() {
    const params = useParams();
    const navigate = useNavigate();

    const [enrollment, actions] = useEnrollment(params.enrollmentId);

    const [isEnrollmentFormOpen, toggleEnrollmentFormOpen] = useBoolean(false);

    const updateEnrollment = useCallback(data => {
        return actions.updateEnrollment(enrollment.id, data)
            .then(() => toggleEnrollmentFormOpen(false));
    }, [enrollment, actions, toggleEnrollmentFormOpen]);

    const updateEnrollmentSchedule = useCallback(data => {
        return actions.updateSchedule(enrollment.id, data);
    }, [enrollment, actions]);

    const deleteEnrollment = useCallback(() => {
        return actions.deleteEnrollment(enrollment.id)
            .then(() => {
                navigate(`/learners/${enrollment.learnerId}`);
            });
    }, [enrollment, actions, navigate]);

    if (!enrollment) return <LoadingIndicator />;

    return (
        <Page className="EnrollmentPage">
            <Page.Header
                breadcrumbs={[
                    { key: 'learner', content: enrollment.learner.fullname, to: enrollment?.learner.url }
                ]}
                title={DomainLabel[enrollment.domain]}
                actions={[
                    (enrollment.hhid && {
                        key: 'hhid',
                        as: 'a',
                        href: getEnrollmentHHUrl(enrollment),
                        target: '_blank',
                        icon: 'link',
                        title: 'Открыть в Hollihop'
                    }),
                    {
                        key: 'edit',
                        title: 'Изменить',
                        icon: 'edit',
                        onClick: toggleEnrollmentFormOpen
                    },
                    <ConfirmButton
                        key="delete"
                        message="Вы действительно хотите удалить обучение?"
                        icon="delete"
                        color="neutral"
                        variant="plain"
                        onConfirm={deleteEnrollment}
                    />
                ]}
            />

            <Page.Content>
                <EnrollmentMeta enrollment={enrollment} />

                <EnrollmentStatus
                    enrollment={enrollment}
                    onUpdate={updateEnrollment}
                />

                <Grid spacing={2}>
                    <Grid.Item xs={3}>
                        <Flex
                            gap="medium"
                            column
                        >
                            <EnrollmentDetails enrollment={enrollment} />
                        </Flex>
                    </Grid.Item>

                    <Grid.Item xs={6}>
                        <Flex
                            gap="medium"
                            column
                        >
                            <EnrollmentLessons enrollment={enrollment} />
                            <EnrollmentComments enrollment={enrollment} />
                        </Flex>
                    </Grid.Item>

                    <Grid.Item xs={3}>
                        <Flex
                            gap="medium"
                            column
                        >
                            <EnrollmentSchedule
                                enrollment={enrollment}
                                onUpdate={updateEnrollmentSchedule}
                            />

                            {/* <EnrollmentPayments
                            enrollment={enrollment}
                        /> */}

                            <EnrollmentCourses enrollment={enrollment} />
                            <EnrollmentMaterials enrollment={enrollment} />
                        </Flex>
                    </Grid.Item>

                    <Grid.Item xs={12}>
                        <EnrollmentTasks enrollment={enrollment} />
                    </Grid.Item>

                    {/* <EnrollmentTrialLesson
                        enrollment={enrollment}
                        onUpdate={updateEnrollment}
                    /> */}
                </Grid>
            </Page.Content>

            <FormDialog
                form="enrollment-form"
                title="Редактирование обучения"
                open={isEnrollmentFormOpen}
                onClose={toggleEnrollmentFormOpen}
            >
                <EnrollmentForm
                    id="enrollment-form"
                    enrollment={enrollment}
                    onSubmit={updateEnrollment}
                />
            </FormDialog>
        </Page>
    );
}